import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { formatDate, formatTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ChatMonitor from "@/components/admin/ChatMonitor";
import { MapPin, MessageSquare, Star, ExternalLink } from "lucide-react";

const tone = { confirmed: "green", in_progress: "blue", completed: "gray", canceled: "red" };
const vTone = { approved: "green", pending_approval: "amber" };

function OutingDetail({ outingId, onClose, onMonitor, onReassign, onCancel }) {
    const { data } = useQuery({ queryKey: ["admin-outing", outingId], queryFn: async () => (await api.get(`/admin/outings/${outingId}`)).data, enabled: !!outingId });
    if (!data) return null;
    const v = data.venue;
    const resv = data.reservation;
    return (
        <Dialog open={!!outingId} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="outing-detail-dialog">
                <DialogHeader>
                    <DialogTitle>{data.activity_emoji} {data.activity_name}</DialogTitle>
                    <DialogDescription>{formatDate(data.date)} · {formatTime(data.start_time)} – {formatTime(data.end_time)} · {data.market}</DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-zinc-500">Status:</span> <Badge tone={tone[data.status]}>{data.status}</Badge></div>
                    <div><span className="text-zinc-500">Venue:</span> <Badge tone={vTone[data.venue_status] || "gray"}>{(data.venue_status || "").replace("_", " ") || "—"}</Badge></div>
                    <div><span className="text-zinc-500">Members:</span> {data.participants.length}</div>
                    <div><span className="text-zinc-500">Feedback:</span> {data.feedback_count} · Messages: {data.message_count}</div>
                </div>

                {/* Venue */}
                <div className="rounded-lg border border-zinc-200 p-3 text-sm" data-testid="outing-venue">
                    {v ? (
                        <div>
                            <p className="font-600 flex items-center gap-1"><MapPin className="h-4 w-4 text-zinc-400" /> {v.name}</p>
                            <p className="text-xs text-zinc-500">{[v.street, v.city, v.state, v.zip_code].filter(Boolean).join(", ")}</p>
                            <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                                {v.rating != null && <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {v.rating}</span>}
                                {v.price_level && <span>{v.price_level}</span>}
                                {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Website</a>}
                            </p>
                        </div>
                    ) : data.venue_status === "pending_approval" ? (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-amber-700">Venue not set — {data.venue_candidate_count} AI suggestion(s) awaiting your approval.</span>
                            <Link to="/admin/venue-approvals"><Button size="sm" variant="outline" data-testid="outing-go-approve-venue">Approve venue <ExternalLink className="ml-1 h-3.5 w-3.5" /></Button></Link>
                        </div>
                    ) : <span className="text-zinc-500">No venue set.</span>}
                </div>

                {/* Reservation */}
                {resv && (
                    <div className="rounded-lg bg-zinc-50 p-3 text-sm" data-testid="outing-reservation">
                        <p>Reservation host: <span className="font-600">{resv.host_temp_emoji} {resv.host_temp_name}</span> · {resv.status}</p>
                        {resv.deadline_at && <p className="text-xs text-zinc-500">Deadline: {new Date(resv.deadline_at).toLocaleString()}</p>}
                    </div>
                )}

                {/* Participants */}
                <div>
                    <p className="mb-2 text-sm font-600">Participants (temp identity → real)</p>
                    <div className="rounded-lg border border-zinc-200 overflow-x-auto">
                        <Table>
                            <TableHeader><TableRow><TableHead>Identity</TableHead><TableHead>Real name</TableHead><TableHead>City</TableHead><TableHead>Attendance</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {data.participants.map((p, i) => (
                                    <TableRow key={i} className={p.canceled ? "opacity-50" : ""}>
                                        <TableCell>{p.temp_emoji} {p.temp_name}</TableCell>
                                        <TableCell className="font-500">{p.real_name || "—"}<span className="block text-xs text-zinc-400">{p.email}</span></TableCell>
                                        <TableCell>{p.city || "—"}</TableCell>
                                        <TableCell><span className={p.attendance === "attended" ? "text-emerald-600" : p.attendance === "no_show" ? "text-red-600" : "text-zinc-400"}>{p.canceled ? "canceled" : p.attendance}</span></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" data-testid="outing-detail-monitor" onClick={() => onMonitor(data.id, `${data.activity_emoji} ${data.activity_name}`)}>
                        <MessageSquare className="mr-1 h-4 w-4" /> Monitor chat
                    </Button>
                    {resv && resv.status !== "booked" && data.status === "confirmed" && (
                        <Button size="sm" variant="outline" data-testid="outing-detail-reassign" onClick={() => onReassign(data.id)}>Reassign host</Button>
                    )}
                    {data.status !== "canceled" && (
                        <Button size="sm" variant="outline" data-testid="outing-detail-cancel" onClick={() => onCancel(data.id)} className="border-red-300 text-red-600 hover:bg-red-50">Cancel outing</Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Outings() {
    const qc = useQueryClient();
    const [monitor, setMonitor] = useState(null);
    const [detail, setDetail] = useState(null);
    const [search, setSearch] = useState("");
    const { data } = useQuery({ queryKey: ["admin-outings"], queryFn: async () => (await api.get("/admin/outings")).data });
    const { data: results } = useQuery({ queryKey: ["chat-search", search], queryFn: async () => (await api.get(`/admin/chat-search?q=${encodeURIComponent(search)}`)).data, enabled: search.length >= 2 });

    const reassign = async (id) => {
        try { const r = await api.post(`/admin/outings/${id}/reassign-host`); toast.success(`Reassigned to ${r.data.host}.`); qc.invalidateQueries({ queryKey: ["admin-outings"] }); qc.invalidateQueries({ queryKey: ["admin-outing", id] }); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const cancelOuting = async (id) => {
        if (!window.confirm("Cancel this outing? Members will be notified and the group chat will close.")) return;
        try { const r = await api.post(`/admin/outings/${id}/cancel`); toast.success(`Outing canceled — ${r.data.notified} members notified.`); qc.invalidateQueries({ queryKey: ["admin-outings"] }); qc.invalidateQueries({ queryKey: ["admin-outing", id] }); setDetail(null); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div>
            <AdminHeader title="Outings" subtitle="Click an outing for full details. Monitor conversations, attendance and reservations." />
            <div className="mb-5">
                <Input placeholder="Search all chats for a word or phrase…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="global-chat-search" />
                {search.length >= 2 && (
                    <div className="mt-2 rounded-lg border border-zinc-200 bg-white p-3" data-testid="chat-search-results">
                        {!results?.length ? <p className="text-sm text-zinc-400">No matches.</p> : results.map((m) => (
                            <div key={m.id} className={`flex items-center justify-between gap-2 border-b border-zinc-100 py-1.5 text-sm last:border-0 ${m.flagged ? "bg-red-50" : ""}`}>
                                <div className="min-w-0"><span className="text-zinc-400">{m.activity} · {m.real_name || m.temp_name}:</span> <span className="text-zinc-700">{m.text}</span></div>
                                <Button size="sm" variant="outline" onClick={() => setMonitor({ id: m.outing_id, title: m.activity })}>Open</Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="space-y-4">
                {(data || []).map((o) => {
                    const resv = o.reservation;
                    return (
                        <div key={o.id} data-testid={`outing-row-${o.id}`} onClick={() => setDetail(o.id)}
                            className="cursor-pointer rounded-lg border border-zinc-200 bg-white p-5 transition-colors hover:border-zinc-400">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <p className="text-lg font-600">{o.activity_emoji} {o.activity_name}</p>
                                    <p className="text-sm text-zinc-500">{formatDate(o.date)} · {formatTime(o.start_time)} – {formatTime(o.end_time)} · {o.venue_name || (o.venue_status === "pending_approval" ? "Venue pending approval" : "No venue")}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge tone={tone[o.status]}>{o.status}</Badge>
                                    <span className="text-xs text-zinc-500">{o.participant_count} members · {o.feedback_count} feedback</span>
                                    <Button size="sm" variant="outline" data-testid={`monitor-chat-${o.id}`} onClick={(e) => { e.stopPropagation(); setMonitor({ id: o.id, title: `${o.activity_emoji} ${o.activity_name}` }); }}>Monitor chat</Button>
                                </div>
                            </div>
                            {resv && (
                                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm">
                                    <span className="text-zinc-600">Reservation host: <span className="font-600">{resv.host_temp_emoji} {resv.host_temp_name}</span><span className="ml-1 text-zinc-400">· {resv.status}</span></span>
                                    {resv.status !== "booked" && o.status === "confirmed" && <Button size="sm" variant="outline" data-testid={`reassign-host-${o.id}`} onClick={(e) => { e.stopPropagation(); reassign(o.id); }}>Reassign host</Button>}
                                </div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                                {o.participants.map((p, i) => (
                                    <span key={i} className="rounded bg-zinc-100 px-2 py-1 text-xs">{p.temp_emoji} {p.temp_name}<span className={`ml-1 ${p.attendance === "attended" ? "text-emerald-600" : p.attendance === "no_show" ? "text-red-600" : "text-zinc-400"}`}>· {p.attendance}</span></span>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            {detail && <OutingDetail outingId={detail} onClose={() => setDetail(null)}
                onMonitor={(id, title) => setMonitor({ id, title })}
                onReassign={reassign} onCancel={cancelOuting} />}
            {monitor && <ChatMonitor outingId={monitor.id} title={monitor.title} onClose={() => setMonitor(null)} />}
        </div>
    );
}
